<template>
  <div
    ref="mediaRoot"
    class="ca-promo-media"
    :class="[
      `ca-promo-media--stats-${visibleStatistics.length}`,
      {
        'ca-promo-media--with-cards': visibleStatistics.length,
        'is-loading': loading,
      },
    ]"
    :aria-busy="loading || undefined"
  >
    <video
      v-if="hasPlayableVideo"
      ref="videoElement"
      class="ca-promo-media__image"
      :poster="imageSource"
      :aria-label="videoAccessibleLabel"
      tabindex="-1"
      muted
      loop
      playsinline
      preload="metadata"
      @play="handleVideoPlay"
      @pause="handleVideoPause"
      @error="handleVideoError"
    >
      <source :src="videoSrc" />
    </video>
    <picture v-else class="ca-promo-media__picture">
      <img
        class="ca-promo-media__image"
        :src="imageSource"
        :srcset="effectiveSrcSet || undefined"
        :sizes="effectiveSrcSet ? sizes || undefined : undefined"
        :alt="hasAuthoredImage ? alt : ''"
        :aria-hidden="hasAuthoredImage ? undefined : 'true'"
        decoding="async"
        @error="handleImageError"
      />
    </picture>

    <button
      v-if="hasPlayableVideo && !loading"
      class="ca-promo-media__video-control"
      type="button"
      :aria-label="videoControlLabel"
      @click="handleVideoControl"
    >
      <span class="ca-promo-media__video-icon-box" aria-hidden="true">
        <PromoVideoPauseIcon class="ca-promo-media__video-icon" />
      </span>
    </button>

    <a
      v-else-if="videoLink && !loading"
      class="ca-promo-media__video-control"
      :href="videoLink"
      :target="videoTarget"
      :rel="videoTarget === '_blank' ? 'noopener noreferrer' : undefined"
      :aria-label="videoLinkAccessibleLabel"
      @click="emit('video-play')"
    >
      <span class="ca-promo-media__video-icon-box" aria-hidden="true">
        <PromoVideoPauseIcon class="ca-promo-media__video-icon" />
      </span>
    </a>

    <span
      v-if="hasPlayableVideo"
      class="ca-promo-media__visually-hidden"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ videoStatusMessage }}
    </span>

    <ul
      v-if="visibleStatistics.length && !loading"
      class="ca-promo-media__statistics"
      role="list"
      :aria-label="relatedContentLabel"
    >
      <li
        v-for="(statistic, index) in visibleStatistics"
        :key="statistic.id ?? index"
        class="ca-promo-media__statistic-item"
      >
        <component
          :is="statistic.url ? 'a' : 'div'"
          class="ca-promo-media__statistic"
          :href="statistic.url || undefined"
          :target="statistic.url ? statistic.target : undefined"
          :rel="
            statistic.url && statistic.target === '_blank'
              ? 'noopener noreferrer'
              : undefined
          "
          :aria-label="
            statistic.url ? cardAccessibleLabel(statistic) : undefined
          "
          @click="statistic.url && emit('card-click', { statistic, index })"
        >
          <CaPromoContentCard
            :title="statistic.title"
            :title-heading-type="statistic.titleHeadingType"
            :description="statistic.description"
            :description-heading-type="statistic.descriptionHeadingType"
            :theme="statistic.theme"
            :icon-src="statistic.iconSrc"
            :icon-name="statistic.iconName"
          />
        </component>
      </li>
    </ul>
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import PromoVideoPauseIcon from "@assets/promo-video-play.svg";
import promoMediaPlaceholder from "@assets/promo-media-placeholder.png";
import CaPromoContentCard from "./CaPromoContentCard.vue";

defineOptions({
  name: "CaPromoMedia",
});

const props = defineProps({
  src: {
    type: String,
    default: "",
  },
  srcSet: {
    type: String,
    default: "",
  },
  sizes: {
    type: String,
    default: "100vw",
  },
  alt: {
    type: String,
    default: "",
  },
  cards: {
    type: Boolean,
    default: true,
  },
  statistics: {
    type: Array,
    default: () => [],
  },
  video: {
    type: Boolean,
    default: false,
  },
  videoSrc: {
    type: String,
    default: "",
  },
  videoLink: {
    type: String,
    default: "",
  },
  videoTarget: {
    type: String,
    default: "_blank",
  },
  videoAccessibleLabel: {
    type: String,
    default: "Vídeo",
  },
  videoLabel: {
    type: String,
    default: "Reproduzir vídeo",
  },
  videoPauseLabel: {
    type: String,
    default: "Pausar vídeo",
  },
  videoLinkLabel: {
    type: String,
    default: "Abrir vídeo",
  },
  videoPlayingAnnouncement: {
    type: String,
    default: "Vídeo em reprodução",
  },
  videoPausedAnnouncement: {
    type: String,
    default: "Vídeo em pausa",
  },
  relatedContentLabel: {
    type: String,
    default: "Conteúdos relacionados",
  },
  newWindowLabel: {
    type: String,
    default: "Abre numa nova janela",
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "card-click",
  "image-error",
  "video-error",
  "video-play",
  "video-state-change",
]);
const mediaRoot = ref(null);
const videoElement = ref(null);
const useFallback = ref(false);
const videoFailed = ref(false);
const isPlaying = ref(false);
const hasVideoStateChanged = ref(false);
let visibilityObserver;

const visibleStatistics = computed(() =>
  props.cards
    ? props.statistics.slice(0, 4).map((statistic, index) => {
        const rawIcon = statistic.icon;
        const iconText = readText(
          statistic.iconName ??
            rawIcon?.name ??
            (typeof rawIcon === "string" ? rawIcon : ""),
        );
        const iconSrc = readUrl(
          statistic.iconSrc ??
            statistic.iconUrl ??
            rawIcon?.src ??
            rawIcon?.url ??
            (isAssetReference(iconText) ? iconText : ""),
        );
        const hasIconButton = isEnabledVariant(
          statistic.iconButton ?? statistic["Icon Button"],
        );
        const hasSelectedIcon = Boolean(
          hasIconButton || iconSrc || normalizeIconName(iconText),
        );

        return {
          ...statistic,
          id: statistic.id ?? index,
          title: readText(statistic.title ?? statistic.text),
          description: readText(statistic.description),
          theme: normalizeCardTheme(statistic.theme, hasSelectedIcon),
          iconSrc,
          iconName: iconSrc ? "" : normalizeIconName(iconText),
          url: readUrl(statistic.url ?? statistic.href ?? statistic.link),
          target:
            readText(statistic.target ?? statistic.link?.target) || "_self",
          accessibleLabel: readText(
            statistic.accessibleLabel ?? statistic.ariaLabel,
          ),
        };
      })
    : [],
);
const imageSource = computed(() =>
  useFallback.value || !props.src ? promoMediaPlaceholder : props.src,
);
const hasAuthoredImage = computed(() => Boolean(props.src || props.srcSet));
const effectiveSrcSet = computed(() =>
  !useFallback.value ? props.srcSet : "",
);
const hasPlayableVideo = computed(
  () => props.video && props.videoSrc && !videoFailed.value,
);
const videoControlLabel = computed(() =>
  isPlaying.value ? props.videoPauseLabel : props.videoLabel,
);
const videoLinkAccessibleLabel = computed(() =>
  [
    props.videoLinkLabel,
    props.videoTarget === "_blank" ? props.newWindowLabel : "",
  ]
    .filter(Boolean)
    .join(". "),
);
const videoStatusMessage = computed(() => {
  if (!hasVideoStateChanged.value) return "";
  return isPlaying.value
    ? props.videoPlayingAnnouncement
    : props.videoPausedAnnouncement;
});

watch(
  () => [props.src, props.srcSet],
  () => {
    useFallback.value = false;
  },
);

watch(
  () => [props.video, props.videoSrc],
  async () => {
    videoFailed.value = false;
    isPlaying.value = false;
    hasVideoStateChanged.value = false;
    await nextTick();
    observeVisibility();
  },
);

watch(isPlaying, (playing) => {
  emit("video-state-change", { playing });
});

onMounted(observeVisibility);

onBeforeUnmount(() => {
  visibilityObserver?.disconnect();
});

function readText(value) {
  if (value && typeof value === "object") {
    const resolvedValue = value.text ?? value.label ?? value.value;
    return typeof resolvedValue === "object"
      ? readText(resolvedValue)
      : String(resolvedValue ?? "");
  }

  return String(value ?? "");
}

function readUrl(value) {
  if (value && typeof value === "object") {
    const resolvedValue = value.url ?? value.href ?? value.src ?? value.value;
    return typeof resolvedValue === "object"
      ? readUrl(resolvedValue)
      : String(resolvedValue ?? "");
  }

  return String(value ?? "");
}

/**
 * Um cartao com icone e sempre clear — o avatar do icone so existe nessa
 * variante, por isso a escolha do autor nao se aplica aqui. Sem icone vale o
 * que o autor escolheu, e light-green quando nao escolheu nada.
 */
function normalizeCardTheme(value, hasSelectedIcon = false) {
  if (hasSelectedIcon) return "clear";

  const normalizedValue = readText(value)
    .split("=")
    .pop()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  return (
    {
      "light-green": "light-green",
      "dark-green": "dark-green",
      inverted: "dark-green",
      clear: "clear",
      transparent: "clear",
    }[normalizedValue] ?? "light-green"
  );
}

function isEnabledVariant(value) {
  return ["1", "on", "true", "yes"].includes(
    readText(value).split("=").pop().toLowerCase().trim(),
  );
}

function isAssetReference(value) {
  return /^(?:https?:|data:|blob:|\/|\.\/|\.\.\/)|\.(?:svg|png|jpe?g|gif|webp)(?:[?#].*)?$/i.test(
    value,
  );
}

function normalizeIconName(value) {
  const normalizedValue = value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

  return (
    {
      movimentos: "movements",
      seguranca: "privacy",
    }[normalizedValue] ?? normalizedValue
  );
}

function cardAccessibleLabel(statistic) {
  const label =
    statistic.accessibleLabel ||
    [statistic.title, statistic.description].filter(Boolean).join(". ");

  return [label, statistic.target === "_blank" ? props.newWindowLabel : ""]
    .filter(Boolean)
    .join(". ");
}

function observeVisibility() {
  visibilityObserver?.disconnect();

  if (!hasPlayableVideo.value || !mediaRoot.value) return;

  if (typeof IntersectionObserver === "undefined") {
    playVideo();
    return;
  }

  visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        playVideo();
      } else {
        videoElement.value?.pause();
      }
    },
    { threshold: 0.35 },
  );
  visibilityObserver.observe(mediaRoot.value);
}

async function playVideo() {
  if (!videoElement.value) return;

  try {
    await videoElement.value.play();
  } catch {
    isPlaying.value = false;
  }
}

function handleVideoControl() {
  if (hasPlayableVideo.value && videoElement.value) {
    if (videoElement.value.paused) {
      playVideo();
      emit("video-play");
    } else {
      videoElement.value.pause();
    }
    return;
  }
}

function handleVideoPlay() {
  hasVideoStateChanged.value = true;
  isPlaying.value = true;
}

function handleVideoPause() {
  hasVideoStateChanged.value = true;
  isPlaying.value = false;
}

function handleVideoError() {
  videoFailed.value = true;
  isPlaying.value = false;
  visibilityObserver?.disconnect();
  emit("video-error");
}

function handleImageError() {
  if (hasAuthoredImage.value && !useFallback.value) {
    useFallback.value = true;
  }

  emit("image-error");
}
</script>

<style scoped lang="scss">
.ca-promo-media {
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: flex-end;
  width: 100%;
  min-width: 0;
  min-height: 520px;
  height: auto;
  padding: var(--size-4);
  overflow: hidden;
  border-radius: var(--ca-promo-media-radius, var(--size-6));
  background-color: var(--ca-color-backgrounds-container-low);

  @include ca-bp("sm") {
    padding: var(--size-5);
  }

  @include ca-bp("lg") {
    min-height: 600px;
  }
}

.ca-promo-media--with-cards {
  @include ca-bp("lg") {
    padding: var(--size-5);
  }
}

.ca-promo-media__image {
  position: absolute;
  z-index: -1;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ca-promo-media__picture {
  position: absolute;
  z-index: -1;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}

.ca-promo-media__picture .ca-promo-media__image {
  z-index: auto;
}

.ca-promo-media__video-control {
  position: absolute;
  top: var(--size-4);
  left: var(--size-4);
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--size-8);
  height: var(--size-8);
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: var(--ca-color-text-body-variant);
  background-color: var(--ca-color-neutrals-inverted);
  box-shadow: 0 2px 3px rgb(0 0 0 / 10%);
  cursor: pointer;
  text-decoration: none;

  &:focus-visible {
    outline: 1px solid var(--ca-color-outline-focus-only);
    outline-offset: 2px;
  }

  @include ca-bp("sm") {
    top: var(--size-5);
    left: var(--size-5);
  }
}

.ca-promo-media__video-icon-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--size-5);
  height: var(--size-5);
}

.ca-promo-media__video-icon {
  display: block;
  width: 16.001px;
  height: 16px;
}

.ca-promo-media__statistics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--size-4);
  width: 100%;
  padding: 0;
  margin: 0;
  list-style: none;
}

.ca-promo-media--stats-1 .ca-promo-media__statistics {
  grid-template-columns: minmax(0, 1fr);
}

.ca-promo-media--stats-3 .ca-promo-media__statistic-item:last-child {
  grid-column: 1 / -1;
}

.ca-promo-media__statistic-item {
  display: flex;
  min-width: 0;
}

.ca-promo-media__statistic {
  display: flex;
  min-width: 0;
  width: 100%;
  height: 100%;
  text-decoration: none;
  color: inherit;
}

a.ca-promo-media__statistic {
  cursor: pointer;

  &:focus-visible {
    outline: 1px solid var(--ca-color-outline-focus-only);
    outline-offset: 2px;
  }
}

.ca-promo-media--stats-3 {
  @media (min-width: 768px) and (max-width: 1023.98px),
    (min-width: 1025px) {
    .ca-promo-media__statistics {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .ca-promo-media__statistic-item:last-child {
      grid-column: auto;
    }
  }
}

.ca-promo-media__visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.ca-promo-media.is-loading::after {
  position: absolute;
  inset: 0;
  content: "";
  background: linear-gradient(
    100deg,
    transparent 20%,
    rgb(255 255 255 / 38%) 50%,
    transparent 80%
  );
  animation: ca-promo-media-loading 1.4s ease-in-out infinite;
}

@keyframes ca-promo-media-loading {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ca-promo-media.is-loading::after {
    animation: none;
  }
}
</style>
